import {
  type CoreFiltersFrontendState,
  transformFrontendStateToQueryArgs,
  unsetNonFilterableDimensionAttributes,
} from '@autone/redux/core';
import { STABLE_ARRAY } from '@autone/utils';
import { get, set, unset } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { type Get, type OmitDeep, type Paths } from 'type-fest';

import { useCoreGeneralFiltersContext } from '../contexts/core-general-filters.context';

type CoreLocationFiltersFrontendState = Pick<
  CoreFiltersFrontendState,
  'geographic' | 'location_cluster' | 'location_groups'
>;

export type CoreLocationGeneralFiltersDimensionAttributePaths = Paths<
  CoreLocationFiltersFrontendState,
  { depth: 1 }
>;

/**
 * Context hook binding all core location filters together.
 */
export const useCoreLocationGeneralFiltersContext = <
  P extends CoreLocationGeneralFiltersDimensionAttributePaths,
>(
  dimensionAggregationPath: P,
) => {
  const [coreGeneralFilters, setCoreGeneralFilters] =
    useCoreGeneralFiltersContext();

  const coreLocationFiltersQueryArg = useMemo(() => {
    const { geographic, location_cluster, location_groups } =
      structuredClone(coreGeneralFilters);
    // Clone one level down because `unset` mutates in place and paths are depth 1.
    const coreLocationFilters = unsetNonFilterableDimensionAttributes({
      geographic,
      location_cluster,
      location_groups,
    });
    // need to typecast because `unset` is a mutable operation
    unset(coreLocationFilters, dimensionAggregationPath);
    return transformFrontendStateToQueryArgs(
      coreLocationFilters as OmitDeep<
        typeof coreLocationFilters,
        typeof dimensionAggregationPath
      >,
    );
  }, [coreGeneralFilters, dimensionAggregationPath]);

  const dimensionAttributeValue = useMemo(() => {
    return get(coreGeneralFilters, dimensionAggregationPath) ?? STABLE_ARRAY;
  }, [coreGeneralFilters, dimensionAggregationPath]);

  const setDimensionAttributeValue = useCallback(
    (value: Get<CoreFiltersFrontendState, P>) => {
      const newValue =
        Array.isArray(value) && value.length === 0 ? undefined : value;
      setCoreGeneralFilters((draft) => {
        set(draft, dimensionAggregationPath, newValue);
      });
    },
    [setCoreGeneralFilters, dimensionAggregationPath],
  );

  return {
    coreLocationFiltersQueryArg,
    dimensionAttributeValue,
    setDimensionAttributeValue,
  };
};
